import { styled } from '@mui/material';
import CollapseElem from 'Components/Collapse';
import DocumentCreationButton from 'Components/DocumentCreationButton';
import { Item } from 'Components/Grid';
import HistoryRow from 'Components/HistoryRow';
import HistoryRowControls from 'Components/HistoryRowControls';
import HistoryRowListing from 'Components/HistoryRowListing';
import HistoryRowVerticalItem from 'Components/HistoryRowVerticalItem';
import HistorySection from 'Components/HistorySection';
import Unit from 'Components/Unit';
import { formatPartialDate } from 'Utility/date';
import { sortDocuments } from 'Utility/randomUtil';
import * as React from 'react';
import { useIntl } from 'react-intl';

const SubHeader = styled('div')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 600,
  fontSize: '2rem',
  margin: '2rem 0 1rem 0',
}));

const NerveBiopsyHistoryRow = ({
  d,
  startEdit,
  fm,
}: {
  d: IDocumentWithTypeV2<'nerveBiopsy'>;
  startEdit: StartEdit;
  fm: (id: string) => string;
}) => {
  const keyFindings: Array<keyof INerveBiopsy> = [
    'amyloidDeposits',
    'inflammatoryCellInfiltrates',
    'vasculitis',
    'demyelination',
    'axonalLoss',
  ];
  return (
    <HistoryRow
      headerText={d.date ? formatPartialDate(d.date) : undefined}
      rowButton={startEdit && <HistoryRowControls document={d} startEdit={startEdit} />}
      controlsMargin={false}
    >
      <>
        <HistoryRowVerticalItem
          header={fm('polyneuroDiagnosticExams.nerveBiopsy.biopsiedNerve')}
          value={
            d.biopsiedNerve === 'other'
              ? fm('polyneuroDiagnosticExams.nerveBiopsy.opts.' + d.biopsiedNerve) + ` - ${d.biopsiedNerveOther || ''}`
              : d.biopsiedNerve
                ? fm('polyneuroDiagnosticExams.nerveBiopsy.opts.' + d.biopsiedNerve)
                : undefined
          }
        />
        <HistoryRowVerticalItem
          header={fm('polyneuroDiagnosticExams.nerveBiopsy.interpretation')}
          value={
            d.interpretation === 'other'
              ? fm('polyneuroDiagnosticExams.nerveBiopsy.opts.' + d.interpretation) +
                ` - ${d.interpretationOther || ''}`
              : d.interpretation
                ? fm('polyneuroDiagnosticExams.nerveBiopsy.opts.' + d.interpretation)
                : undefined
          }
        />
        <SubHeader>{fm('polyneuroDiagnosticExams.nerveBiopsy.keyFindings')}</SubHeader>
        {keyFindings.map(key => (
          <HistoryRowVerticalItem
            key={key}
            header={fm('polyneuroDiagnosticExams.nerveBiopsy.' + key)}
            value={d[key] ? fm('general.' + d[key]) : undefined}
          />
        ))}
        <div style={{ marginTop: '4rem' }}>
          <HistoryRowVerticalItem
            header={fm('polyneuroDiagnosticExams.additionalInformation')}
            value={d.additionalInformation}
          />
        </div>
      </>
    </HistoryRow>
  );
};

const SkinBiopsyHistoryRow = ({
  d,
  startEdit,
  fm,
}: {
  d: IDocumentWithTypeV2<'skinBiopsy'>;
  startEdit: StartEdit;
  fm: (id: string) => string;
}) => {
  return (
    <HistoryRow
      headerText={d.date ? formatPartialDate(d.date) : undefined}
      key={d._id}
      rowButton={startEdit && <HistoryRowControls document={d} startEdit={startEdit} />}
      controlsMargin={false}
    >
      <>
        <HistoryRowVerticalItem
          header={fm('polyneuroDiagnosticExams.skinBiopsy.biopsySite')}
          value={
            d.biopsySite === 'other'
              ? fm('polyneuroDiagnosticExams.skinBiopsy.opts.' + d.biopsySite) + ` - ${d.biopsySiteOther || ''}`
              : d.biopsySite
                ? fm('polyneuroDiagnosticExams.skinBiopsy.opts.' + d.biopsySite)
                : undefined
          }
        />
        <HistoryRowVerticalItem
          header={fm('polyneuroDiagnosticExams.skinBiopsy.intraepidermalNerveFiberDensity')}
          value={
            d.intraepidermalNerveFiberDensity ? (
              <Unit unit={fm('polyneuroDiagnosticExams.skinBiopsy.fibersMM')} fontWeight={'bold'}>
                {d.intraepidermalNerveFiberDensity}
              </Unit>
            ) : undefined
          }
        />
        <HistoryRowVerticalItem
          header={fm('polyneuroDiagnosticExams.skinBiopsy.belowReferenceValue')}
          value={d.belowReferenceValue ? fm('general.' + d.belowReferenceValue) : undefined}
        />
        <HistoryRowVerticalItem
          header={fm('polyneuroDiagnosticExams.additionalInformation')}
          value={d.additionalInformation}
        />
      </>
    </HistoryRow>
  );
};

const DiagnosticExamHistory = ({ documents, startEdit }: IOwnProps) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const csfDocs = documents
    .filter(d => d._type === 'csfExam')
    .toSorted((n1, n2) => sortDocuments([{ type: 'date', sortField: 'date' }])(n1, n2));
  const nervebiopsyDocs = documents
    .filter(d => d._type === 'nerveBiopsy')
    .toSorted((n1, n2) => sortDocuments([{ type: 'date', sortField: 'date' }])(n1, n2));
  const skinbiopsyDocs = documents
    .filter(d => d._type === 'skinBiopsy')
    .toSorted((n1, n2) => sortDocuments([{ type: 'date', sortField: 'date' }])(n1, n2));

  return (
    <>
      <HistorySection
        headerText={fm(`polyneuroDiagnosticExams.csfExamTitle`)}
        newButton={
          <DocumentCreationButton
            name="csfExam"
            text={'polyneuroDiagnosticExams.newcsfExam'}
            onClick={e => {
              startEdit({}, 'csfExam')(e);
            }}
            width={16}
            height={3}
            fontSize={14}
            alternate={true}
          />
        }
      >
        <HistoryRowListing
          documents={csfDocs}
          headers={
            <>
              <Item xs={2}>{fm('general.date')}</Item>
              <Item xs={3}>{fm('polyneuroDiagnosticExams.csfExam.albuminocytologicDissociation')}</Item>
              <Item xs={2}>{fm('polyneuroDiagnosticExams.csfExam.oligoclonalBands')}</Item>
              <Item xs={2}>{fm('polyneuroDiagnosticExams.additionalInformation')}</Item>
              <Item xs={3} />
            </>
          }
          contentFormat={(d): React.JSX.Element => (
            <>
              <Item xs={2} style={{ fontWeight: 600 }}>
                {formatPartialDate(d.date)}
              </Item>
              <Item xs={3} style={{ fontWeight: 600 }}>
                {d.albuminocytologicDissociation
                  ? fm('polyneuroDiagnosticExams.csfExam.opts.' + d.albuminocytologicDissociation)
                  : '-'}
              </Item>
              <Item xs={2}>
                {d.oligoclonalBands ? fm('polyneuroDiagnosticExams.csfExam.opts.' + d.oligoclonalBands) : '-'}
              </Item>
              <Item xs={2}>{d.additionalInformation || '-'}</Item>
              <Item xs={3}>
                <HistoryRowControls
                  document={d}
                  startEdit={d => e => {
                    startEdit(d)(e);
                  }}
                  buttonDimensions={{ width: 16 }}
                />
              </Item>
            </>
          )}
        />
      </HistorySection>
      <HistorySection
        headerText={fm(`polyneuroDiagnosticExams.nerveBiopsyTitle`)}
        newButton={
          <DocumentCreationButton
            name="nerveBiopsy"
            text={'polyneuroDiagnosticExams.newnerveBiopsy'}
            onClick={e => {
              startEdit({}, 'nerveBiopsy')(e);
            }}
            width={16}
            height={3}
            fontSize={14}
            alternate={true}
          />
        }
      >
        {nervebiopsyDocs[0] ? (
          <NerveBiopsyHistoryRow d={nervebiopsyDocs[0]} startEdit={startEdit} fm={fm} />
        ) : undefined}

        {nervebiopsyDocs.length > 1 ? (
          <CollapseElem
            localeIDs={{
              showMessage: 'polyneuroDiagnosticExams.showMore',
              hideMessage: 'polyneuroDiagnosticExams.hide',
            }}
            amount={nervebiopsyDocs.length - 1}
          >
            {nervebiopsyDocs.slice(1).map(d => (
              <NerveBiopsyHistoryRow key={d._id} d={d} startEdit={startEdit} fm={fm} />
            ))}
          </CollapseElem>
        ) : undefined}
      </HistorySection>
      <HistorySection
        headerText={fm(`polyneuroDiagnosticExams.skinBiopsyTitle`)}
        newButton={
          <DocumentCreationButton
            name="skinBiopsy"
            text={'polyneuroDiagnosticExams.newskinBiopsy'}
            onClick={e => {
              startEdit({}, 'skinBiopsy')(e);
            }}
            width={16}
            height={3}
            fontSize={14}
            alternate={true}
          />
        }
      >
        {skinbiopsyDocs[0] ? <SkinBiopsyHistoryRow d={skinbiopsyDocs[0]} startEdit={startEdit} fm={fm} /> : undefined}

        {skinbiopsyDocs.length > 1 ? (
          <CollapseElem
            localeIDs={{
              showMessage: 'polyneuroDiagnosticExams.showMore',
              hideMessage: 'polyneuroDiagnosticExams.hide',
            }}
            amount={skinbiopsyDocs.length - 1}
          >
            {skinbiopsyDocs.slice(1).map(d => (
              <SkinBiopsyHistoryRow key={d._id} d={d} startEdit={startEdit} fm={fm} />
            ))}
          </CollapseElem>
        ) : undefined}
      </HistorySection>
    </>
  );
};

interface IOwnProps {
  documents: (
    | IDocumentWithTypeV2<'csfExam'>
    | IDocumentWithTypeV2<'nerveBiopsy'>
    | IDocumentWithTypeV2<'skinBiopsy'>
  )[];
  startEdit: StartEdit;
  view?: IView;
}

export default DiagnosticExamHistory;
